






















































import { Component, Vue, Watch, Prop } from "vue-property-decorator";

import Cart from "@/views/Order/Cart.vue";
import { Getter, Action, Mutation } from "vuex-class";
import billingAPI from "@/services/billing";
import accountsAPI from "@/services/accounts";
import OrderHelpers, { Order } from "@/models/order";
import loader from "@/components/loader.vue";
import { Address } from "../../models/address";
import { Account } from "../../models/account";
import { Package } from "../../models/package";
import { debounce, cancel } from "typescript-debounce-decorator";
import { ServiceMatrix } from "@/models/serviceMatrix";

@Component({
  components: {
    Cart,
    loader,
  },
})
export default class OrderView extends Vue {
  // order getters
  private get readOnly() {
    return OrderHelpers.ReadOnly(this.order);
  }
  private get hasServiceMatrix() {
    return OrderHelpers.HasServiceMatrix(this.order);
  }
  private get hasAccount() {
    return OrderHelpers.HasAccount(this.order);
  }
  private get hasPackages() {
    return OrderHelpers.HasPackages(this.order);
  }
  private get hasRegion() {
    return OrderHelpers.HasRegion(this.order);
  }
  private get needShippingAddress() {
    return OrderHelpers.NeedShippingAddress(this.order);
  }
  private get needShippingPackage() {
    return OrderHelpers.NeedShippingPackage(this.order);
  }
  private get hasShippingPackage() {
    return OrderHelpers.HasShippingPackage(this.order);
  }
  private get hasShippingAddress() {
    return OrderHelpers.HasShippingAddress(this.order);
  }
  private get hasBillingAddress() {
    return OrderHelpers.HasBillingAddress(this.order);
  }
  private get confirmationInfoComplete() {
    return OrderHelpers.ConfirmationInfoComplete(this.order);
  }
  private get needsApproval() {
    return OrderHelpers.NeedsApproval(this.order);
  }

  private get shippingAddressMatchRegion() {
    if (
      !OrderHelpers.HasShippingAddress(this.order) ||
      !this.order.delivery_address ||
      !this.services
    ) {
      return false;
    }
    return (
      this.services.dst_region == "" ||
      this.services.dst_region.toLowerCase() ==
      (this.order.delivery_address.country || "").toLowerCase()
    );
  }

  // helpers
  get pathLastSection() {
    const p = this.$route.path.split("/");
    return p[p.length - 1];
  }

  get navItems() {
    return [
      {
        title: "Services",
        url: "services_matrix",
        checked: this.hasServiceMatrix,
        clickable: true,
      },
      {
        title: "Customer",
        url: "customer",
        checked: this.hasAccount,
        clickable: this.hasServiceMatrix,
      },
      // {
      //   title: "Region",
      //   url: "destination_region",
      //   checked: this.hasRegion,
      //   clickable: this.hasServiceMatrix,
      // },
      {
        title: "Packages",
        url: "packages",
        checked: this.hasPackages,
        clickable: this.hasServiceMatrix,
        hide: this.readOnly,
      },
      {
        title: "Bundles",
        url: "bundles",
        checked: this.hasPackages,
        clickable: this.hasServiceMatrix,
        hide: this.readOnly,
      },
      {
        title: "Shipping Address",
        url: "shipping_address",
        checked:
          this.hasShippingAddress &&
          (
            this.needShippingPackage
              ? this.hasShippingPackage
              : true
          ),
        hide: !this.needShippingAddress,
        clickable: this.hasServiceMatrix,
      },
      {
        title: "Billing Address",
        url: "billing_address",
        checked: this.hasBillingAddress,
        clickable: this.hasServiceMatrix,
      },
      {
        title: "Confirm Order",
        url: "confirm",
        checked: this.confirmationInfoComplete,
        clickable: this.hasServiceMatrix,
      },
    ].filter((i) => (i.hide ? false : true));
  }
  @Prop({ default: "new" }) public newId?: string | number;

  @Getter("serviceMatrix") serviceMatrix!: ServiceMatrix[];
  get services() {
    return this.serviceMatrix.find(
      (mtix) => mtix.id === this.order!.service_matrix_id
    );
  }

  @Mutation("setLastErr") public setLastErr: any;
  public loading: string | boolean = "Loading";
  @Action("nav/gotoView") public gotoView: any;

  @Getter("globalSelectedAccount") private globalSelectedAccount?: Account;
  @Getter("packages") private availablePackages?: Package[];
  @Action("setGlobalSelectedAccount") private setGlobalSelectedAccount: any;

  // all child components will emit events to the main order component to update this order object
  private order: Order = OrderHelpers.New();

  public async created() {
    if (this.newId === "new") {
      await this.newOrder();
    } else {
      await this.loadOrder(Number(this.newId));
    }
  }

  @debounce(200, { leading: false })
  public async updateOrder() {
    this.setLastErr(null);
    // if (this.order.packages.length === 0 || this.order.account_id === 0) { return; }

    try {
      if (this.order.id && this.order.id > 0) {
        this.order = await billingAPI.updateOrder(this.order.id, this.order);
      } else {
        this.order = await billingAPI.createOrder(this.order);
      }
    } catch (e) {
      this.setLastErr(e);
      return;
    }
  }

  public async checkoutOrder() {
    this.setLastErr(null);
    if (!this.order.id) {
      return;
    }
    if (this.order.packages.length === 0 || this.order.account_id === 0) {
      return;
    }

    try {
      this.order = await billingAPI.updateOrder(this.order.id, this.order);
      if (!this.order.id) {
        return;
      }

      this.order = await billingAPI.checkoutOrder(this.order.id, this.order);
      this.order = OrderHelpers.New();
      this.gotoView("default");
    } catch (e) {
      this.setLastErr(e);
      return;
    }
  }
  private async setOrder(o: Order) {
    this.order = o;
    if (this.order.account_id) {
      this.loading = "Loading order account " + this.order.account_id;
      this.setSelectedAccount(
        await accountsAPI.GetAccountByID(this.order.account_id)
      );

      if (this.$route.name === "Select Customer") {
        this.$router.push("packages");
      }
    }

    // update path to reflect id in path
    const path = this.$route.path;
    const newPath = path.replace("/new", `/${this.order.id}`);
    if (path !== newPath) {
      this.$router.replace(newPath);
    }
  }

  // loading existing or new order
  // skip for now to allow quotes
  private async newOrder() {
    this.loading = "Creating new order";
    try {
      const o = OrderHelpers.New();
      // if (this.globalSelectedAccount && this.globalSelectedAccount.id) {
      //   o.account_id = this.globalSelectedAccount.id;
      // }
      this.setOrder(await billingAPI.createOrder(o));
    } catch (e) {
      this.setLastErr(e);
    }
    this.loading = false;
  }

  private async loadOrder(orderID: number) {
    this.loading = "Loading order " + orderID;
    try {
      const o = await billingAPI.getOrder(orderID);
      this.setOrder(o);
    } catch (e) {
      this.setLastErr(e);
    }
    this.loading = false;
  }

  // order setters
  private setSelectedAccount(acc?: Account) {
    this.setGlobalSelectedAccount(acc);
    this.order.account_id = acc ? acc.id : 0;
    this.updateOrder();
  }

  private orderSetSalesConsultant(id: number) {
    this.order.sales_agent_id = id;
  }

  private orderSetShippingAddress(address?: Address) {
    this.order.delivery_address = address;
  }

  private orderSetBillingAddress(address?: Address) {
    this.order.billing_address = address;
  }

  private async setOrderFields(ob: any, sync?: boolean, nextPath?: string) {
    // console.log("got update call", ob, "sync: ", sync, "nextPath:", nextPath);
    if (ob) {
      this.order = {
        ...this.order,
        ...ob,
      };

      if (ob.billing_account) {
        this.order.billing_account_id = ob.billing_account.id;
        this.updateAccountBillingAccount();
      }
      if (ob.account) {
        this.order.account_id = ob.account.id;
        if (ob.account.billing_account && ob.account.billing_account.id > 0) {
          this.order.billing_account = ob.account.billing_account;
          this.order.billing_account_id = ob.account.billing_account.id;
        }
      }
    }
    if (nextPath) {
      this.$router.push(nextPath);
    }
    if (sync) {
      await this.updateOrder();
    }
  }

  private async updateAccountBillingAccount() {
    if (
      this.order.account &&
      this.order.account.id > 0 &&
      this.order.account.billing_account_id !== this.order.billing_account_id
    ) {
      this.order.account.billing_account_id = this.order.billing_account_id;
      try {
        accountsAPI.UpdateAccount(this.order.account);
      } catch (e) {
        this.setLastErr(e);
      }
    }
  }

  private updatePackageQty(
    shortCode: string,
    qty?: number | boolean,
    inc?: number | boolean,
    dec?: number | boolean,
    discountRate?: number,
    bundleID?: number,
  ) {
    shortCode = String(shortCode).split("-")[0]; // ensure shortcode
    if (!this.order) {
      return;
    }

    let p = this.order.packages.find(
      (pkg: Package) =>
        String(pkg.code).split("-")[0] === shortCode &&
        (pkg.discount_rate || 0) === (discountRate || 0) &&
        (pkg.bundle_id || 0) === (bundleID || 0)
    );

    if (!p && this.availablePackages) {
      p = this.availablePackages.find(
        (pkg: Package) => String(pkg.code).split("-")[0] === shortCode
      );
      if (!p) {
        return;
      }

      // copy
      p = JSON.parse(JSON.stringify(p));
      if (!p) {
        return;
      }
      p.discount_rate = discountRate || 0;
      this.order.packages.push(p);
      console.log("=== pkg not found, adding pkg to order:", shortCode);
    }
    if (!p) {
      return;
    }

    p.qty = Number(p.qty) || 0;
    if (qty) {
      p.qty = Number(qty);
    }
    if (inc) {
      p.qty = Number(p.qty) + Number(inc);
    }
    if (dec) {
      p.qty = Number(p.qty) - Number(dec);
    }
    if (bundleID) {
      p.bundle_id = bundleID;
    }

    if (p.qty < 1) {
      const idx = this.order.packages.indexOf(p);
      this.order.packages.splice(idx, 1);
    }

    console.log("=== increased qty:", shortCode, p.qty);

    if (!p.bundle_id && p.shippable) { // if it's not part of a bundle and the new package is shippable, remove current shipping package as it would need recalculation
      this.order.packages = this.order.packages.filter((p) => !(p.category == "shipping" && p.id == 0))
    }

    // for some reason need to re-copy, otherwise updates doesn't work
    this.order.packages = JSON.parse(JSON.stringify(this.order.packages));
    this.order.total = 0;
    this.order.total_estimated = 0;
    this.setOrderFields({ packages: this.order.packages }, true);
  }

  private destroyed() {
    cancel(this.updateOrder);
  }
}
